// The DEV/LOCAL fixture switcher's dialog — sign in as any seeded dev account without logging out.
//
// SHARED by both shells (artist + roster): the whole point of the reshape was that a band account could not
// reach the roster lenses, so a switcher living inside either shell's own chrome would have the same problem
// one level up. One dialog, one nav button, mounted the same way in both.
//
// TWO-PANE (2026-09-12): the flat list grew past one column with the fleshed-out fixture cast, so it is now a
// WIDE, STATIC-HEIGHT master-detail — the left rail is split into org-type sections (Artists / Labels /
// Management / Booking); pick an org or artist and its people appear on the right, each with their role in a
// chip. Category and role both come from the server (fixture graph → seat_titles); the pane height is fixed so
// the modal does not resize as you move between a two-person band and a fourteen-person one.
//
// The gate is `useCanSwitchFixtureAccounts` — a non-production lane, on the web, signed in as a `.test`
// account. The server re-checks BOTH ends; this only avoids rendering a control that would 404.
import { useState } from "react";
import { Modal, Tag } from "@cntrl/ui";
import { AvMark } from "../shared/AvMark.js";
import {
  useActingAs,
  useFixtureAccounts,
  useFixtureReturn,
  useFixtureSwitch,
  type FixtureAccount,
} from "../../app/hooks/useDevFixtureSwitch.js";
import styles from "./FixtureSwitchDialog.module.scss";

type TenantGroup = {
  name: string;
  items: FixtureAccount[];
  category: string | null;
  pro: boolean;
};

// The left-rail sections, in order. Anything the graph could not categorize falls to a trailing "Other".
const CATEGORY_ORDER = ["artist", "label", "management", "booking"] as const;
const CATEGORY_LABEL: Record<string, string> = {
  artist: "Artists",
  label: "Labels",
  management: "Management",
  booking: "Booking",
};

export function FixtureSwitchDialog({
  onClose,
  onNavigate,
}: {
  onClose: () => void;
  onNavigate: (to: string) => void;
}) {
  const { accounts, isLoading, failed } = useFixtureAccounts(true);
  const switchTo = useFixtureSwitch(onNavigate);
  const returnToSelf = useFixtureReturn(onNavigate);
  const actingAs = useActingAs();
  const [returning, setReturning] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);

  // Preserve the server's ordering (crew first, then org seats grouped by lens, then bands) rather than
  // re-sorting inside a tenant — those groups are the three lenses in a stable order, which is what is tested.
  // Merged by NAME, not by adjacency: the server lists your own crew first and everyone else after, so a band's
  // seatless people (a seeded partner, ART-239) arrive in a second run further down — grouping only consecutive
  // rows split one band into two rail entries, both lit by the same selection.
  const groups: TenantGroup[] = [];
  const byName = new Map<string, TenantGroup>();
  for (const a of accounts) {
    const known = byName.get(a.tenant);
    if (known) known.items.push(a);
    else {
      const g = { name: a.tenant, items: [a], category: a.category, pro: a.pro };
      byName.set(a.tenant, g);
      groups.push(g);
    }
  }

  // Split the rail into org-type sections. A tenant's category is its accounts' shared category.
  const sections = [...CATEGORY_ORDER, null]
    .map((cat) => ({
      key: cat ?? "other",
      label: cat ? CATEGORY_LABEL[cat] : "Other",
      groups: groups.filter((g) =>
        cat === null ? !CATEGORY_ORDER.includes(g.category as never) : g.category === cat,
      ),
    }))
    .filter((s) => s.groups.length > 0);

  // The right pane follows the left. Default to the tenant you are currently IN (the group holding your own
  // row) so the dialog opens on your own context, else the first group. `selectedTenant` wins once you click,
  // but is ignored if it no longer names a group (the account list can refetch under you).
  const selfTenant = groups.find((g) => g.items.some((i) => i.isSelf))?.name;
  const activeName =
    (selectedTenant && groups.some((g) => g.name === selectedTenant) && selectedTenant) ||
    selfTenant ||
    groups[0]?.name ||
    null;
  const active = groups.find((g) => g.name === activeName) ?? null;

  // The owner/admin (the badged root account) leads its tenant's people; the sort is stable, so everyone else
  // keeps the server's order behind it.
  const people = active
    ? [...active.items].sort((a, b) => (b.badge ? 1 : 0) - (a.badge ? 1 : 0))
    : [];

  const empty = !isLoading && (failed || accounts.length === 0);

  return (
    <Modal
      title="View as"
      size="2xl"
      bodyLayout="flush"
      onClose={onClose}
    >
      <div className={styles.shell}>
        {/* The way OUT of a swap. It lived on the standing banner until 2026-09-01; with the banner gone,
            this dialog is the only place it can be, and it has to be here rather than nowhere — a reload
            also returns you to yourself (the server withholds the re-issue token for an impersonated
            session, so the swapped bearer is memory-only), but discovering that is not a control. */}
        {actingAs && (
          <div className={styles.acting}>
            <p className={styles.actingWho}>
              Currently acting as <b>{actingAs.displayName}</b>
              <span className={styles.actingEmail}>{actingAs.email}</span>
            </p>
            <button
              type="button"
              className={styles.return}
              disabled={returning}
              onClick={() => {
                // Closed on SETTLE rather than up front: the return is a round trip through a bearer swap
                // and a cache scorch, and the disabled "Returning…" label is the only feedback it is happening.
                setReturning(true);
                void returnToSelf().finally(() => {
                  setReturning(false);
                  onClose();
                });
              }}
            >
              {returning ? "Returning…" : "Return to my account"}
            </button>
          </div>
        )}

        {isLoading && <p className={styles.note}>Loading accounts…</p>}

        {empty && (
          <p className={styles.note}>
            No fixture accounts available. This needs the seeded account graph —
            see <code>deploy/fixtures/README.md</code>.
          </p>
        )}

        {!isLoading && !empty && (
          <div className={styles.panes}>
            {/* LEFT — orgs/artists, split by type. Pick one to reveal its people. A rail against the edge. */}
            <nav className={styles.tenants} aria-label="Organizations and artists">
              {sections.map((section) => (
                <div key={section.key} className={styles.tenantSection}>
                  <h4 className={styles.tenantSectionName}>{section.label}</h4>
                  {section.groups.map((group) => (
                    <button
                      key={group.name}
                      type="button"
                      className={styles.tenant}
                      aria-pressed={group.name === activeName}
                      onClick={() => setSelectedTenant(group.name)}
                    >
                      <span className={styles.tenantLead}>
                        <span className={styles.tenantName}>{group.name}</span>
                        {group.pro && (
                          <Tag tone="accent" size="sm">
                            Pro
                          </Tag>
                        )}
                      </span>
                      <span className={styles.tenantCount}>{group.items.length}</span>
                    </button>
                  ))}
                </div>
              ))}
            </nav>

            {/* RIGHT — the selected tenant's people, each with a role chip. Clicking a row swaps into it. */}
            <div className={styles.people}>
              {active && <h3 className={styles.peopleHead}>{active.name}</h3>}
              <ul className={styles.list}>
                {people.map((a) => (
                  <li key={a.personId}>
                    <button
                      type="button"
                      className={styles.item}
                      // Your own row is listed rather than hidden, so the dialog reads as the whole cast and
                      // you can see where you are in it. Selecting it is refused by the server (400) anyway.
                      disabled={a.isSelf}
                      onClick={() => {
                        onClose();
                        void switchTo(a.personId);
                      }}
                    >
                      <span className={styles.itemName}>
                        {a.displayName}
                        {a.isSelf && <span className={styles.you}> · you</span>}
                      </span>
                      <span className={styles.chips}>
                        {/* The seat that can open Team and Organization settings — accent, so it stands out
                            of a column of neutral role chips. */}
                        {a.badge && (
                          <Tag tone="accent" size="sm">
                            {a.badge}
                          </Tag>
                        )}
                        {a.role && (
                          <Tag tone="neutral" size="sm">
                            {a.role}
                            {/* ART-171: a full-access band member wears the AV mark here too. */}
                            <AvMark roleKey={a.roleKey} />
                          </Tag>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
